import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar, TextField, Button, Container, Typography, Box, MenuItem, CircularProgress, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import AuthService from "../services/AuthService";
import userService, { getUserCreationErrorMessage } from '../services/UserService';
import planService from '../services/PlanService';
import permissionGroupService from '../services/PermissionGroupService';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js'; // Import the validation function
import {
    LLM_MODELS_BY_VENDOR,
    LLM_VENDORS,
} from '../services/LlmCatalogService';

const defaultLlmVendor = LLM_VENDORS[0]?.value || 'OpenAI';
const defaultLlmProvider = LLM_MODELS_BY_VENDOR[defaultLlmVendor]?.[0]?.value || 'gpt-5-mini';

const fieldSx = {
    backgroundColor: 'background.default',
    borderRadius: 1,
    '& .MuiOutlinedInput-root': {
        minHeight: 48,
        alignItems: 'center',
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '0.95rem',
        lineHeight: '22px',
        padding: '13px 14px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.95rem',
    },
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 13px) scale(1)',
    },
};

const selectFieldSx = {
    ...fieldSx,
    '& .MuiOutlinedInput-input': {
        fontSize: '0.95rem',
        lineHeight: '22px',
        padding: '13px 14px',
        textAlign: 'left',
    },
    '& .MuiSelect-select': {
        alignItems: 'center',
        display: 'flex',
        minHeight: '22px',
        padding: '13px 32px 13px 14px',
        textAlign: 'left',
    },
};

const groupSx = {
    mx: 0,
    px: 1.5,
    pt: 0.5,
    pb: 1.25,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    textAlign: 'left',
};

const UserForm = ({ onUserAddOrUpdate, onCancel, onBusyChange, profile, operType, user }) => {
    const isCustomerAdd = operType === 'add' && profile === 'customer';
    const isMemberForm = profile === 'member';
    const emptyUser = {
        name: '',
        email: '',
        phoneNumber: '', // Add phoneNumber to state
        profile: profile,
        planId: '',
        permissionGroupIds: [],
        clientName: '',
        companyName: '',
        address: '',
        llmVendor: defaultLlmVendor,
        llmProvider: defaultLlmProvider,
        agentPrompt: '',
    };

    const [formData, setFormData] = useState((operType === 'add') ? emptyUser : {
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber, // Add phoneNumber to state
        profile: user.profile,
        planId: user.planId || '',
        permissionGroupIds: Array.isArray(user.permissionGroupIds) ? user.permissionGroupIds : [],
        clientName: user.clientName || '',
        companyName: user.companyName || '',
        address: user.address || '',
        llmVendor: user.llmVendor || defaultLlmVendor,
        llmProvider: user.llmProvider || defaultLlmProvider,
        agentPrompt: user.agentPrompt || '',
    });
    const { t } = useTranslation();
    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState([]);
    const [pgroups, setPgroups] = useState([]);
    const [pgroupsLoading, setPgroupsLoading] = useState(false);
    const [pgroupsError, setPgroupsError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [phoneError, setPhoneError] = useState('');

    const token = AuthService.getAccessToken();

    useEffect(() => {
        if (typeof onBusyChange !== 'function') return undefined;
        onBusyChange(loading);
        return () => onBusyChange(false);
    }, [loading, onBusyChange]);

    useEffect(() => {
        if (operType !== 'add' || profile !== 'customer') return;

        let ignore = false;
        planService.getPlans()
            .then((data) => {
                if (ignore) return;
                const list = Array.isArray(data) ? [...data] : [];
                list.sort((a, b) => String(a?.planName || a?.planId || '').localeCompare(String(b?.planName || b?.planId || '')));
                setPlans(list);
                setFormData((prev) => {
                    if (prev.planId || list.length === 0) return prev;
                    return {
                        ...prev,
                        planId: list[0]?.planId || '',
                    };
                });
            })
            .catch((error) => {
                console.error('Failed to load plans for client form', error);
                if (!ignore) setPlans([]);
            });

        return () => {
            ignore = true;
        };
    }, [operType, profile]);

    useEffect(() => {
        if (!isMemberForm) return undefined;
        let ignore = false;
        setPgroupsLoading(true);
        setPgroupsError('');
        permissionGroupService.getAssignableGroups()
            .then((data) => { if (!ignore) setPgroups((Array.isArray(data) ? data : []).filter((g) => g?.active !== false)); })
            .catch((e) => { if (!ignore) { setPgroups([]); setPgroupsError('Failed to load permission groups.'); } })
            .finally(() => { if (!ignore) setPgroupsLoading(false); });
        return () => { ignore = true; };
    }, [isMemberForm]);

    const sortedPlans = useMemo(() => {
        const list = Array.isArray(plans) ? [...plans] : [];
        list.sort((a, b) => String(a?.planName || a?.planId || '').localeCompare(String(b?.planName || b?.planId || '')));
        return list;
    }, [plans]);

    if (!token) {
        console.error('No access token available');
        return null;
    }

    const validateUsername = (username) => {
        if (/[A-Z]/.test(username)) {
            setUsernameError('Username must be in lowercase.');
            return false;
        } else {
            setUsernameError('');
            return true;
        }
    };

    const isValidLocalPhoneNumber = (phoneNumber) => {
        const trimmed = String(phoneNumber || '').trim();
        if (!trimmed.startsWith('0')) return false;
        if (!/^[0-9\s()-]+$/.test(trimmed)) return false;

        const digits = trimmed.replace(/\D/g, '');
        return /^0\d{9,10}$/.test(digits);
    };

    const validatePhoneNumber = (phoneNumber) => {
        const trimmed = String(phoneNumber || '').trim();
        if (trimmed && !isValidLocalPhoneNumber(trimmed) && !isValidPhoneNumber(trimmed)) {
            setPhoneError('Invalid phone number format.');
            return false;
        } else {
            setPhoneError('');
            return true;
        }
    };

    const handleChange = (e) => {
        const { name } = e.target;
        const value = name === 'name' ? e.target.value.toLowerCase() : e.target.value;
        setFormData((prev) => {
            if (name === 'llmVendor') {
                const provider = LLM_MODELS_BY_VENDOR[value]?.[0]?.value || '';
                return {
                    ...prev,
                    llmVendor: value,
                    llmProvider: provider,
                };
            }
            return {
                ...prev,
                [name]: value
            };
        });
        if (name === 'name') {
            validateUsername(value);
        } else if (name === 'phoneNumber') {
            validatePhoneNumber(value);
        }
    };

    const togglePgroup = (groupId) => {
        setFormData((prev) => {
            const cur = Array.isArray(prev.permissionGroupIds) ? prev.permissionGroupIds : [];
            const next = cur.includes(groupId) ? cur.filter((id) => id !== groupId) : [...cur, groupId];
            return { ...prev, permissionGroupIds: next };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        if (!validateUsername(formData.name)) {
            return;
        }
        if (!validatePhoneNumber(formData.phoneNumber)) {
            return;
        }
        try {
            setErrorMessage('');
            setLoading(true);
            let response;
            if (operType === 'add') {
                response = await userService.addUser(formData);
            } else {
                response = await userService.updateUser({ ...formData, username: formData.name });
            }
            setLoading(false);
            console.log(response);
            if (response.status === 200 || response.status === 201) {
                setResponseMessage('User created successfully!');
                setErrorMessage('');
                onUserAddOrUpdate();
            }
        } catch (error) {
            setLoading(false);
            setErrorMessage(getUserCreationErrorMessage(error, t));
            setResponseMessage('');
        }
    };

    return (
        <Container
            maxWidth={isCustomerAdd ? false : 'sm'}
            sx={{
                position: 'relative',
                mt: 1.5,
                mb: 2,
                p: 2.5,
                width: '100%',
                maxWidth: isCustomerAdd ? 1100 : undefined,
                boxShadow: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
            }}
        >

            <Snackbar
                open={Boolean(errorMessage)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ width: 'calc(100% - 32px)', maxWidth: 600 }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    closeText={t('users.dismissError')}
                    onClose={() => setErrorMessage('')}
                    sx={{
                        width: '100%',
                        textAlign: 'left',
                        fontSize: '1rem',
                        boxShadow: 6,
                        bgcolor: '#c62828',
                        color: '#fff',
                        '& .MuiAlert-icon': { color: 'inherit' },
                    }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>

            <Box
                component="form"
                onSubmit={handleSubmit}
                aria-busy={loading}
                sx={{ textAlign: 'center' }}
            >
                <Box
                    sx={{
                        display: isCustomerAdd ? 'grid' : 'block',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 0.9fr) minmax(460px, 1.1fr)' },
                        gap: isCustomerAdd ? 3 : 0,
                        alignItems: 'start',
                    }}
                >
                    <Box
                        component="fieldset"
                        disabled={loading}
                        sx={{
                            ...groupSx,
                            mb: isCustomerAdd ? 0 : 1.5,
                            width: '100%',
                            maxWidth: isCustomerAdd ? 480 : undefined,
                        }}
                    >
                        <Typography
                            component="legend"
                            sx={{
                                px: 1,
                                color: 'text.secondary',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('users.clientDetails', formData.profile === 'customer' ? 'Client Details' : 'User Details')}
                        </Typography>
                        {operType === 'edit' && (
                            <TextField disabled={loading}
                                fullWidth
                                label={t('users.name')}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                margin="normal"
                                error={Boolean(usernameError)}
                                helperText={usernameError}
                                slotProps={{
                                    input: { readOnly: true }
                                }}
                                sx={{
                                    ...fieldSx,
                                    backgroundColor: 'action.disabledBackground',
                                    '& .MuiInputBase-input': {
                                        ...fieldSx['& .MuiInputBase-input'],
                                        color: 'text.disabled',
                                        cursor: 'not-allowed',
                                    },
                                }}
                            />
                        )}
                        {operType === 'add' && (
                            <TextField disabled={loading}
                                fullWidth
                                required
                                label={formData.profile === 'customer' ? t('users.clientId', 'Client ID') : t('users.name')}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                margin="normal"
                                error={Boolean(usernameError)}
                                helperText={usernameError}
                                sx={fieldSx}
                            />
                        )}
                        <TextField disabled={loading}
                            fullWidth
                            required
                            type="email"
                            label={t('users.email')}
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            margin="normal"
                            sx={fieldSx}
                        />
                        {operType === 'add' && formData.profile === 'customer' && (
                            <>
                                <TextField disabled={loading}
                                    fullWidth
                                    select
                                    label={t('users.plan', 'Plan')}
                                    name="planId"
                                    value={formData.planId}
                                    onChange={handleChange}
                                    margin="normal"
                                    required
                                    sx={selectFieldSx}
                                >
                                    {sortedPlans.map((plan) => (
                                        <MenuItem key={plan.planId} value={plan.planId}>
                                            {plan.planName || plan.planId}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <TextField disabled={loading}
                                    fullWidth
                                    label={t('users.clientName', 'Client Name')}
                                    name="clientName"
                                    value={formData.clientName}
                                    onChange={handleChange}
                                    margin="normal"
                                    sx={fieldSx}
                                />

                                <TextField disabled={loading}
                                    fullWidth
                                    label={t('users.companyName', 'Company Name')}
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    margin="normal"
                                    sx={fieldSx}
                                />
                            </>
                        )}

                        <TextField disabled={loading}
                            fullWidth
                            // required
                            label={t('users.phoneNumber')}
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            margin="normal"
                            error={Boolean(phoneError)}
                            helperText={phoneError}
                            sx={fieldSx}
                        />

                        {isMemberForm && (
                            <Box sx={{ mt: 2, textAlign: 'left' }}>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
                                    {t('users.permissionGroups', 'Permission Groups')}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1 }}>
                                    {t('users.permissionGroupsHint', 'Every user gets base access. Add one or more groups to grant more.')}
                                </Typography>
                                {pgroupsLoading ? (
                                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{t('users.loadingGroups', 'Loading…')}</Typography>
                                ) : pgroups.length === 0 ? (
                                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{pgroupsError || t('users.noGroups', 'No additional permission groups available.')}</Typography>
                                ) : (
                                    <FormGroup>
                                        {pgroups.map((g) => (
                                            <Box key={g.groupId} sx={{ mb: 0.5 }}>
                                                <FormControlLabel
                                                    control={<Checkbox disabled={loading} checked={(formData.permissionGroupIds || []).includes(g.groupId)} onChange={() => togglePgroup(g.groupId)} />}
                                                    label={t(`permissionGroups.groupNames.${g.groupId}`, g.name || g.groupId)}
                                                />
                                                {(() => {
                                                    const desc = t(`permissionGroups.groupDescriptions.${g.groupId}`, g.description || '');
                                                    return desc ? <Typography sx={{ ml: 4, mt: -0.5, fontSize: '0.76rem', color: 'text.secondary', lineHeight: 1.4 }}>{desc}</Typography> : null;
                                                })()}
                                            </Box>
                                        ))}
                                    </FormGroup>
                                )}
                                <Typography sx={{ mt: 1, fontSize: '0.76rem', color: 'text.secondary', lineHeight: 1.45 }}>
                                    {t('users.groupPlanHint', "Access also depends on the client's plan: a group never unlocks a feature the plan excludes.")}
                                </Typography>
                            </Box>
                        )}

                        {operType === 'add' && formData.profile === 'customer' && (
                            <TextField disabled={loading}
                                fullWidth
                                label={t('users.address', 'Address')}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                margin="normal"
                                multiline
                                minRows={2}
                                sx={fieldSx}
                            />
                        )}
                    </Box>

                    {isCustomerAdd && (
                        <Box
                            component="fieldset"
                            disabled={loading}
                            sx={{ ...groupSx, width: '100%', minWidth: 0, maxWidth: 560 }}
                        >
                            <Typography
                                component="legend"
                                sx={{
                                    px: 1,
                                    color: 'text.secondary',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                }}
                            >
                                {t('users.agentSettings', 'Agent Settings')}
                            </Typography>
                            <TextField disabled={loading}
                                fullWidth
                                required
                                select
                                label={t('users.llmVendor', 'LLM Vendor')}
                                name="llmVendor"
                                value={formData.llmVendor}
                                onChange={handleChange}
                                margin="normal"
                                sx={selectFieldSx}
                            >
                                {LLM_VENDORS.map((vendor) => (
                                    <MenuItem key={vendor.value} value={vendor.value}>
                                        {vendor.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                required
                                select
                                label={t('users.llmProvider', 'LLM Provider')}
                                name="llmProvider"
                                value={formData.llmProvider}
                                onChange={handleChange}
                                margin="normal"
                                disabled={loading || !formData.llmVendor}
                                sx={selectFieldSx}
                            >
                                {(LLM_MODELS_BY_VENDOR[formData.llmVendor] || []).map((model) => (
                                    <MenuItem key={model.value} value={model.value}>
                                        {model.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField disabled={loading}
                                fullWidth
                                label={t('users.prompt', 'Prompt')}
                                name="agentPrompt"
                                value={formData.agentPrompt}
                                onChange={handleChange}
                                margin="normal"
                                multiline
                                minRows={6}
                                sx={fieldSx}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            onCancel();
                        }}
                        sx={{
                            mt: 1,
                            py: 1.2,
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            width: '150px',
                            borderRadius: 2,
                        }}
                        disabled={loading}

                    >
                        {t('users.cancel')}
                    </Button>

                    <Button
                        type="submit"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        variant="contained"
                        color="primary"
                        sx={{
                            mt: 1,
                            py: 1.2,
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            width: '150px',
                            borderRadius: 2,
                        }}
                        disabled={loading || Boolean(usernameError) || Boolean(phoneError) || (operType === 'add' && formData.profile === 'customer' && (!formData.planId || !formData.llmVendor || !formData.llmProvider))}
                    >
                        {loading ? t('files.saving', 'Saving...') : t('users.submit')}
                    </Button>
                </Box>

                {responseMessage && (
                    <Typography color="success.main" sx={{ mt: 2, fontSize: '1rem', fontWeight: '500' }}>
                        {responseMessage}
                    </Typography>
                )}

            </Box>
        </Container>
    );
};

export default UserForm;
